import { Dispatch, SetStateAction, useEffect } from "react";
import { Banner, FoodsTextInput, FormBox, HelperBox, Typography, useFormBaseContext } from "@foodpilot/foods";
import { Box, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import { SupplierFormAppearance, SupplierFormType } from "../../SupplierForm.types";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SectionCard, SectionContent, SectionHeader } from "src/components/EntityForm";

const supplierSchema = z.object({
    name: z.string().min(1),
    clientId: z.string().min(1),
});

type SupplierFormData = z.infer<typeof supplierSchema>;

type Page1Props = {
    appearance?: SupplierFormAppearance;
    supplier: SupplierFormType;
    setSupplier: Dispatch<SetStateAction<SupplierFormType>>;
    existingSupplier?: Company;
    progress?: { current: number; total: number };
};

const Page1 = (props: Page1Props) => {
    if (props.appearance !== "product-v2") {
        return <LegacyPage1 {...props} />;
    }

    return <ProductV2Page1 {...props} />;
};
export default Page1;

const ProductV2Page1 = (props: Page1Props) => {
    const { t } = useTranslation();
    return (
        <SectionCard data-testid="supplier-identity-section">
            <SectionHeader
                progress={props.progress}
                title={t("general_information", "General information")}
                subtitle={t("supplier.form.identity.subtitle", "Identify the supplier in your organization.")}
            />
            <SectionContent sx={(theme) => ({ paddingBottom: theme.spacing(2) })}>
                <BaseInformation {...props} />
            </SectionContent>
        </SectionCard>
    );
};

const BaseInformation = (props: Page1Props) => {
    const { supplier, setSupplier } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        control,
        formState: { errors },
    } = useForm<SupplierFormData>({
        resolver: zodResolver(supplierSchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            name: supplier.name ?? "",
            clientId: supplier.clientId ?? "",
        },
    });
    const nameLabel = t("nom_du_fournisseur");
    const codeLabel = t("code_fournisseur");

    return (
        <Stack gap={theme.spacing(3)}>
            <Box
                data-testid="supplier-identity-fields"
                sx={{
                    display: "grid",
                    gap: theme.spacing(3),
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    [theme.breakpoints.down("md")]: {
                        gridTemplateColumns: "minmax(0, 1fr)",
                    },
                }}
            >
                <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                        <Stack data-testid="supplier-name-field" gap={theme.spacing(1)} minWidth={0} width="100%">
                            <Typography component="label" htmlFor="supplier-name" variant="body-semibold">
                                {`${nameLabel}* :`}
                            </Typography>
                            <FoodsTextInput
                                {...field}
                                error={!!errors.name}
                                ExtraInputProps={{
                                    inputProps: {
                                        "aria-label": nameLabel,
                                        "aria-required": true,
                                        id: "supplier-name",
                                        required: true,
                                    },
                                }}
                                onChange={(value: string) => {
                                    field.onChange(value);
                                    setSupplier((prevState: SupplierFormType) => ({
                                        ...prevState,
                                        name: value ?? null,
                                    }));
                                }}
                            />
                        </Stack>
                    )}
                />
                <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                        <Stack data-testid="supplier-code-field" gap={theme.spacing(1)} minWidth={0} width="100%">
                            <Typography component="label" htmlFor="supplier-code" variant="body-semibold">
                                {`${codeLabel}* :`}
                            </Typography>
                            <FoodsTextInput
                                {...field}
                                error={!!errors.clientId}
                                ExtraInputProps={{
                                    inputProps: {
                                        "aria-describedby": "supplier-code-help",
                                        "aria-label": codeLabel,
                                        "aria-required": true,
                                        id: "supplier-code",
                                        required: true,
                                    },
                                }}
                                onChange={(value: string) => {
                                    field.onChange(value);
                                    setSupplier((prevState: SupplierFormType) => ({
                                        ...prevState,
                                        clientId: value ?? null,
                                    }));
                                }}
                            />
                            <Box
                                id="supplier-code-help"
                                data-testid="supplier-code-info"
                                sx={{
                                    "& > .MuiStack-root": {
                                        alignItems: "center",
                                        paddingBlock: theme.spacing(1),
                                    },
                                }}
                            >
                                <Banner
                                    variant="themed"
                                    colors={{
                                        backgroundColor: theme.palette.primary[50],
                                        color: theme.palette.primary[700],
                                        sideBorder: theme.palette.primary[600],
                                    }}
                                >
                                    {t(
                                        "le_code_fournisseur_correspond_a_la_maniere_dont_vous_marquer_vos_fournisseurs_dans_votre_entreprise.",
                                    )}
                                </Banner>
                            </Box>
                        </Stack>
                    )}
                />
            </Box>
        </Stack>
    );
};

const LegacyPage1 = (props: Page1Props) => {
    const theme = useTheme();

    return (
        <Box sx={{ display: "flex", gap: theme.spacing(2) }}>
            <FormBox sx={{ flex: 3 }}>
                <LegacyBaseInformation {...props} />
            </FormBox>
        </Box>
    );
};

const LegacyBaseInformation = (props: Page1Props) => {
    const { supplier, setSupplier } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { lockForward, unlockForward } = useFormBaseContext();
    const {
        control,
        formState: { errors, isValid },
    } = useForm<SupplierFormData>({
        resolver: zodResolver(supplierSchema),
        mode: "onChange",
        reValidateMode: "onBlur",
        defaultValues: {
            name: supplier.name ?? "",
            clientId: supplier.clientId ?? "",
        },
    });
    const nameLabel = t("nom_du_fournisseur");
    const codeLabel = t("code_fournisseur");

    useEffect(() => {
        if (isValid) {
            unlockForward();
            return;
        }

        lockForward(t("form.invalidstep", "Complete the required fields"));
    }, [isValid, lockForward, t, unlockForward]);

    return (
        <Stack gap={theme.spacing(6.25)}>
            <Controller
                name="name"
                control={control}
                defaultValue=""
                render={({ field }) => (
                    <Stack gap={theme.spacing(1)} maxWidth="70%">
                        <Typography component="label" htmlFor="legacy-supplier-name" variant="h5">
                            {`${nameLabel}* :`}
                        </Typography>
                        <FoodsTextInput
                            {...field}
                            error={!!errors.name}
                            ExtraInputProps={{
                                inputProps: {
                                    "aria-label": nameLabel,
                                    "aria-required": true,
                                    id: "legacy-supplier-name",
                                    required: true,
                                },
                            }}
                            onChange={(value: string) => {
                                field.onChange(value);
                                setSupplier((current) => ({ ...current, name: value ?? null }));
                            }}
                        />
                    </Stack>
                )}
            />
            <Stack gap={theme.spacing(1)}>
                <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                        <Stack gap={theme.spacing(1)} maxWidth="70%">
                            <Typography component="label" htmlFor="legacy-supplier-code" variant="h5">
                                {`${codeLabel}* :`}
                            </Typography>
                            <FoodsTextInput
                                {...field}
                                error={!!errors.clientId}
                                ExtraInputProps={{
                                    inputProps: {
                                        "aria-describedby": "legacy-supplier-code-help",
                                        "aria-label": codeLabel,
                                        "aria-required": true,
                                        id: "legacy-supplier-code",
                                        required: true,
                                    },
                                }}
                                onChange={(value: string) => {
                                    field.onChange(value);
                                    setSupplier((current) => ({ ...current, clientId: value ?? null }));
                                }}
                            />
                        </Stack>
                    )}
                />
                <HelperBox id="legacy-supplier-code-help">
                    <Typography variant="body">
                        {t(
                            "le_code_fournisseur_correspond_a_la_maniere_dont_vous_marquer_vos_fournisseurs_dans_votre_entreprise.",
                        )}
                    </Typography>
                </HelperBox>
            </Stack>
            <Typography variant="body">{t("form.mandatory_field")}</Typography>
        </Stack>
    );
};
